import * as React from "react";
import { useMemo } from "react";
import styled from "@mui/material/styles/styled";
import { AgeQuestion } from "./AgeQuestion";
import { GenderQuestion } from "./GenderQuestion";
import { DocQuestion } from "./DocQuestion";
import { OpenQuestion } from "./OpenQuestion";
import { ClosedQuestion } from "./ClosedQuestion";
import {
  ConversationQuestionType as QTypes,
  QualifiedFeature,
} from "fe-common/graphql";
import { Answer } from "../../solverHooks/useConversation";
import { usePatient } from "../../state/patientContext";
import { useTranslate } from "../../hooks/useTranslate";
import { PatientBackground } from "../../background/patientBackground";
import { PregnancyQuestion } from "./PregnancyQuestion";
import { AllergyQuestion } from "./allergies/allergyQuestion";
import { MedicationQuestion } from "./medications/medicationQuestion";
import { FreeTextQuestion } from "./FreeTextQuestion";
import { usePatientFeatureFlags } from "../../hooks/usePatientFeatureFlags";
import { KahunPatientLinkButton } from "./kahunPatientLinkButton";
import { FormQuestion } from "./formQuestion";
import { PatientFacingQuestion } from "../../types/patientConversationAliasing";
import { VisitTypeQuestion } from "./specialQuestions/visitType/visitTypeQuestion";
import { NumericTimingQuestion } from "./rangeQuestions/numericQuestions/numericTimingQuestion";
import { UserEnterSymptomsMode } from "../search/searchComponent";
import { DateQuestion } from "./date/DateQuestion";
import { MultipleComplaintsQuestion } from "./chiefComplaint/multipleComplaintsQuestion";
//region [[ Styles ]]

const QuestionElementView = styled("div")(({ theme }) => ({
  display: "flex",
  width: "100%",
}));

export const QuestionContents = styled("div")({
  display: "flex",
  width: "100%", // Necessary for multiple selection and overlay questions
  flexDirection: "column",
  "&:not(:last-of-type)": {
    padding: "5px 0",
  },
  "&:last-of-type": {
    paddingTop: "5px",
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface QuestionElementProps {
  question: PatientFacingQuestion;
  index: number;
  onAnswer?: (
    questionType: QTypes,
  ) => (qfs: QualifiedFeature[], skipped: boolean, metadata: any) => void;
  answer: Answer;
  miniView?: boolean;
  prevAns: Answer;
}
export interface QuestionElementByTypeProps extends QuestionElementProps {
  isEntropy: boolean;
  showKahunInsights: boolean;
}
//endregion [[ Props ]]

//region [[ Functions ]]
export const QuestionElementByType = ({
  question: q,
  answer,
  onAnswer: createAnswerCallback,
  showKahunInsights,
  miniView,
  isEntropy,
  index,
  prevAns,
  ...props
}: QuestionElementByTypeProps) => {
  const { translate } = useTranslate();

  const { gender, exchanges } = usePatient();

  const ccAsFreeText = usePatientFeatureFlags("ccAsFreeText");

  const questionType = useMemo(() => {
    return q?.questionType;
  }, [q]);

  const onAnswer = useMemo(() => createAnswerCallback(questionType), [
    questionType,
    createAnswerCallback,
  ]);

  if (!q) return null;
  switch (q.questionType) {
    case QTypes.Age:
      return (
        <AgeQuestion onAnswer={onAnswer} answer={answer} miniView={miniView} />
      );
    case QTypes.Gender:
      return (
        <GenderQuestion
          onAnswer={onAnswer}
          answer={answer}
          miniView={miniView}
        />
      );
    case QTypes.Pregnancy:
      return (
        <QuestionContents className={"question-contents"}>
          <PregnancyQuestion
            onAnswer={onAnswer}
            question={q}
            gender={gender}
            answer={answer}
            miniView={miniView}
            canSkip
          />
        </QuestionContents>
      );
    case QTypes.VolunteerFamilyHistory:
    case QTypes.VolunteerBackgroud:
      return (
        <PatientBackground question={q} onAnswer={onAnswer} answer={answer} />
      );
    case QTypes.MultipleChiefComplaint:
      return <MultipleComplaintsQuestion onAnswer={onAnswer} answer={answer} />;
    case QTypes.ChiefComplaint:
      return (
        <QuestionContents className={"question-contents"}>
          <DocQuestion text={translate("QUESTION_CHIEF_COMPLAINT")} />
          <OpenQuestion
            canSkip={false}
            gender={gender}
            onAnswer={onAnswer}
            answer={answer}
            miniView={miniView}
            userEnterSymptom={
              ccAsFreeText
                ? UserEnterSymptomsMode.FREE_TEXT
                : UserEnterSymptomsMode.TELL_US
            }
          />
        </QuestionContents>
      );
    case QTypes.OtherComplaint:
      const otherComplaintCount = exchanges
        .filter((e) => e.type === QTypes.OtherComplaint)
        .findIndex((f) => f.question.question === q);
      const randomPrefix = otherComplaintCount % 3;
      return (
        <QuestionContents className={"question-contents"}>
          <DocQuestion
            text={translate("QUESTION_OTHER_COMPLAINT_" + randomPrefix)}
          />
          <OpenQuestion
            canSkip={true}
            gender={gender}
            onAnswer={onAnswer}
            answer={answer}
          />
        </QuestionContents>
      );
    case QTypes.ReasonForVisit:
      return (
        <VisitTypeQuestion
          question={q}
          gender={gender}
          onAnswer={onAnswer}
          answer={answer}
          miniView={miniView}
        />
      );
    case QTypes.BackgroundSuggestion:
    case QTypes.Suggestion: {
      return (
        <QuestionContents className={"question-contents"}>
          <ClosedQuestion
            onAnswer={onAnswer}
            question={q}
            gender={gender}
            answer={answer}
            isEntropy={isEntropy}
            miniView={miniView}
            previousAnswer={prevAns}
          />
        </QuestionContents>
      );
    }
    case QTypes.Allergies:
      return (
        <AllergyQuestion
          onAnswer={onAnswer}
          answer={answer}
          miniView={miniView}
        />
      );
    case QTypes.Medications:
      return (
        <MedicationQuestion
          onAnswer={onAnswer}
          answer={answer}
          question={q}
          miniView={miniView}
        />
      );
    case QTypes.Final:
      if (showKahunInsights) {
        return (
          <KahunPatientLinkButton>
            <OpenQuestion
              canSkip={true}
              onAnswer={onAnswer}
              prefixText={"STOP_CRITERIA_TEXT"}
              gender={gender}
              answer={answer}
              miniView={miniView}
            />
          </KahunPatientLinkButton>
        );
      }
      return (
        <OpenQuestion
          canSkip={true}
          onAnswer={onAnswer}
          prefixText={"STOP_CRITERIA_TEXT"}
          gender={gender}
          answer={answer}
          miniView={miniView}
        />
      );
    case QTypes.InsufficientData:
      return (
        <OpenQuestion
          canSkip={false}
          onAnswer={onAnswer}
          prefixText={"NO_MATCHING_DD"}
          gender={gender}
          answer={answer}
          miniView={miniView}
        />
      );
    case QTypes.FreeText:
      return (
        <FreeTextQuestion
          question={q}
          isFreeTextSuggestion={true}
          questionText={
            q.question?.text ||
            q.suggestions?.[0]?.question?.text ||
            translate("FREE_TEXT_QUESTION")
          }
          placeHolder={
            q.suggestions?.[0]?.suggestion?.feature?.patientDescription
          }
          answer={answer}
          onAnswer={onAnswer}
          miniView={miniView}
          previousAnswer={prevAns}
        />
      );
    case QTypes.FreeTextShort:
      return (
        <FreeTextQuestion
          question={q}
          questionText={q.question.text}
          isFreeTextSuggestion={true}
          placeHolder={q.question.text}
          answer={answer}
          onAnswer={onAnswer}
          miniView={miniView}
          minRows={1}
          maxRows={1}
        />
      );
    case QTypes.Date:
      return (
        <DateQuestion onAnswer={onAnswer} question={q} miniView={miniView} />
      );
    case QTypes.Duration: {
      //backward compatability for restoring old conversation
      return (
        <QuestionContents className={"question-contents"}>
          <NumericTimingQuestion
            answer={answer}
            onAnswer={onAnswer}
            question={q}
            miniView={miniView}
          />
        </QuestionContents>
      );
    }
    case QTypes.Form: {
      return (
        <QuestionContents>
          <FormQuestion answer={answer} onAnswer={onAnswer} question={q} />
        </QuestionContents>
      );
    }
    default:
      return null;
  }
};

//endregion [[ Functions ]] //

export const QuestionElement = ({ ...props }: QuestionElementProps) => {
  const { exchanges } = usePatient();

  //TODO CHEN: Is this needed?
  const isEntropy = () => {
    const lastExchange = exchanges[props.index - 1];

    return (
      lastExchange &&
      lastExchange.type === QTypes.OtherComplaint &&
      lastExchange &&
      // checking also skip for backwards compatibility of a bug in saving conversations
      lastExchange.answer?.skip
    );
  };

  const showKahunInsights = usePatientFeatureFlags(
    "showPatientCardLink",
  ) as boolean;

  return (
    <QuestionElementView className={"question-element-view"}>
      <QuestionElementByType
        {...props}
        isEntropy={isEntropy()}
        showKahunInsights={showKahunInsights}
      />
    </QuestionElementView>
  );
};
